// mapChunks.ts — BETA 22.98 · BOUNDLESS MAP · FLOOR v2 (no-pattern procedural)
//
// Dylan's floor+camera fix pack v2: the ground is NOT tiled images anymore.
// It's built like one continuous ruined lot:
//   L1 flat asphalt base (one color everywhere — zero seam potential)
//   L2 low-frequency brightness patches (world-space, cross-chunk)
//   L3 speckle noise (tiny, uniform density)
//   L4 hand-made asphalt ART as round FEATHERED PATCHES (the 3 Dead Lot
//      paintings become organic islands at random angle/scale — not tiles)
//   L5 faded road/parking paint fragments (long, cross-chunk, code-drawn)
//   L6 keyed decals (cracks/oil/blood/tire/trash/arrows) with per-decal
//      rotation/scale/flip/alpha AND per-REGION density variation so no two
//      screens carry the same amount of detail (anti-pattern rule)
//
// Chunks are generation/performance buckets ONLY. Every layer above L1 seeds
// off WORLD-SPACE cells, so any element near a chunk border is drawn
// identically by both neighboring chunks — borders cannot exist visually.
//
// Enterable-rooms note (pack doc 08): buildings later render as PROPS above
// this ground with their own front-wall fade pass — nothing in this module
// needs to change for that; keep building interiors OUT of the ground cache.
//
// KILL-SWITCH: set BOUNDLESS_MAP = false to restore the old room map wholesale.

import { MAPDEV } from './mapDevViewer';   // BETA 23.00 — dev layer toggles (all true in gameplay)

export const BOUNDLESS_MAP = true;

// ─── BETA 22.98 · MAP-BUILDING MODE ─────────────────────────────────────────
// Dylan: "turn off enemies for now. we are building the map. one thing at a
// time." While TRUE: no enemies spawn or update — the world is a quiet
// walkable lot so map iteration is instant. FLIP TO FALSE the moment map
// work is signed off.
export const MAP_BUILD_NO_ENEMIES = true;

// BETA 23.01 — WEAPON DEPTH & SMART PLACEMENT kill-switch: attached weapon
// sprites join Greg's render group + slide around solid props. FALSE =
// legacy always-on-top weapons.
export const WEAPON_DEPTH_GROUP = true;

// BETA 23.13 — WORLD PROPS (plan P5 pulled forward): wrecked cars, lamps,
// tires, cones, junk — seeded per chunk, footprint collision, NE cast
// shadows, y-sorted with the player. Kill-switch for the whole system.
export const MAP_PROPS = true;
// BETA 23.76 — N2: real approved-house sprites (warped fronts + approved backs) placed on
// the neighborhood street lots. Kill-switch for the whole house layer.
export const MAP_HOUSES = true;

// BETA 23.19 — THE LIGHTING PASS (plan P3): near-night ambient multiply +
// real light pools from lamps/fires/machines/shrines. Kill-switch.
export const MAP_LIGHTING = true;

// BETA 23.23 — REAL PATHS (Dylan: raised walkways POI-to-POI with step-up
// shadows + lamp lines; replaces the removed L4 circles). Kill-switch.
export const MAP_PATHS = true;

// BETA 23.26 — SURGE VEINS: procedural pulsing purple cracks (canon).
export const MAP_SURGE_VEINS = true;

// ── BETA 23.08 · GROUND FORESHORTENING (Step 0 of the mockup plan) ─────────
// The camera is an ~80-degree tilted top-down ("top-down oblique"). A tilted
// camera NEVER sees a ground mark full-height: everything lying on the floor
// renders vertically squashed. All ground-layer content (art patches, decals,
// paint) draws through this squash, and ground-mark rotation is locked near
// horizontal (a rotated-to-vertical oil stain would break the illusion).
// Standing objects (props/characters) are NOT squashed — they draw full
// height at their feet. That contrast IS the 2.5D look.
// BETA 23.09 — CORRECTED to the MEASURED value: forensic analysis of the
// vision mockup shows ground circles squashed to 0.33-0.37 of their width
// (~70-75° camera). The 23.08 value (0.72) was ~2× too gentle — that's why
// the tilt still read weak. Applies to STRAIGHT-DOWN-painted content only;
// the new at-angle substrate tiles carry their own baked foreshortening.
export const GROUND_SQUASH = 0.38;

/** World-units per chunk. */
export const CHUNK_WU = 1024;
/** Offscreen resolution per chunk (0.5 px/wu). */
const CHUNK_PX = 512;
const PX_PER_WU = CHUNK_PX / CHUNK_WU;
/** Max cached chunk canvases (LRU). */
const CACHE_CAP = 48;

// ── BETA 23.09 · SUBSTRATE: painted seamless base tiles ────────────────────
// The floor's base is now PAINTED MATERIAL, not a flat color (gap audit: the
// mockup ground is ~90% painted; our old floor was ~80% flat fill). Four
// seamless cel-style at-angle asphalt variants, seeded per 512wu world tile
// with rarity weights — the Diablo 2 "Random Tiles" anti-repetition pattern.
// No flips/rotation (seamless edges must meet unflipped). Falls back to the
// old flat fill until the art is loaded/present.
// 23.14 — ONE seamless base tile, period (Dylan: "SEE THE PLUS SIGN.
// BREAKING IMMERSION. we need the base to be a seamless perfectly repeatable
// texture floor. then add things on top"). The mixed-variant system WAS the
// plus sign: each offset variant wraps seamlessly onto ITSELF, but two
// DIFFERENT variants meeting share no edge content → a visible seam cross at
// every 4-corner where variants differ. A single self-seamless tile has
// identical edges by construction, so the grid disappears; anti-repetition
// is the job of the layers ABOVE (brightness cells, patches, decals, props —
// all seeded). If painted variants ever return they must be AUTHORED to
// share identical border pixels with 01 (the Diablo tiles do exactly this).
const BASE_TILE_URLS = [
  { url: '/map/deadlot/base_asphalt_01e.webp', weight: 1 },   // 23.42: warm-earth regrade (mockup mood; per-pixel op, seamlessness untouched)   // 23.32: wrap-crossfaded — seam gradient MEASURED below interior texture (provably seamless)   // 23.29: rust-red crack lines neutralized to dark recess tones (Dylan: cracks looked fake)   // 23.25: luminance-flattened (wrap-aware) — the old tile's bright/dark edge lean printed a visible 1024wu grid (Dylan: "you can see the individual squares")
];
const BASE_TILE_WEIGHT_TOTAL = BASE_TILE_URLS.reduce((s, t) => s + t.weight, 0);
const BASE_TILE_WU = 512;   // 23.29: half-size render = pebble detail 2× finer (Dylan: "texture on the ground is too big")   // 23.09b: 512 → 1024 — halves the repeat frequency (1024px source = 1:1 at chunk res)

const PATCH_URLS = [
  '/map/deadlot/ground_01.webp',
  '/map/deadlot/ground_02.webp',
  '/map/deadlot/ground_03.webp',
];

// kind drives per-zone bias (P2 macro zones): crack | stain | debris | paint
type DecalKind = 'crack' | 'stain' | 'debris' | 'paint';
const DECALS: Array<{ url: string; wu: number; weight: number; kind: DecalKind }> = [
  { url: '/map/deadlot/decal_crack_cel.webp',     wu: 300, weight: 4, kind: 'crack' },   // 23.09 cel restyle
  { url: '/map/deadlot/decal_oil_cel.webp',       wu: 170, weight: 3, kind: 'stain' },   // 23.09 cel restyle
  { url: '/map/deadlot/decal_blood_splat.webp',   wu: 120, weight: 3, kind: 'stain' },
  { url: '/map/deadlot/decal_trash_pile.webp',    wu: 110, weight: 2, kind: 'debris' },
  { url: '/map/deadlot/decal_parking_arrow.webp', wu: 200, weight: 1, kind: 'paint' },
  // BETA 23.07 — Sonnet art batch (casings failed QA, regenerate later)
  // 23.39 — rocks were TOP-lit (read as upside-down craters; Dylan caught
  // it); flipped vertically so the low SW sun hits their south faces.
  // Renamed to b-files per the cache-bust rule.
  { url: '/map/deadlot/decal_rocks_01b.webp',         wu: 130, weight: 3, kind: 'debris' },
  { url: '/map/deadlot/decal_rocks_02b.webp',         wu: 170, weight: 2, kind: 'debris' },
  { url: '/map/deadlot/decal_concrete_chunk_01.webp', wu: 150, weight: 2, kind: 'debris' },
  { url: '/map/deadlot/decal_bones_01.webp',          wu: 75,  weight: 1, kind: 'debris' },   // 23.25: Dylan — "way too big"
  { url: '/map/deadlot/decal_broken_glass_01.webp',   wu: 120, weight: 2, kind: 'debris' },
  { url: '/map/deadlot/decal_paper_scraps_01.webp',   wu: 110, weight: 2, kind: 'debris' },
];

// ── BETA 23.08 · P2 MACRO ZONES ─────────────────────────────────────────────
// 8192wu world cells, each typed — the map gets NEIGHBORHOODS instead of one
// uniform texture soup (pack doc 09 macro/meso/micro). Zone factors scale
// each layer's density; kind bias skews which decals appear there.
type ZoneName = 'open_lane' | 'cracked_lot' | 'garage_stain' | 'trashy_corner' | 'clean_pad';
interface ZoneDef {
  decals: number; paint: number; patches: number;
  kindBias: Partial<Record<DecalKind, number>>;
}
const ZONES: Record<ZoneName, ZoneDef> = {
  open_lane:     { decals: 0.55, paint: 1.7, patches: 0.7, kindBias: { paint: 2.0 } },   // 23.09b: never naked
  cracked_lot:   { decals: 1.0,  paint: 0.8, patches: 1.2, kindBias: { crack: 2.0 } },
  garage_stain:  { decals: 1.3,  paint: 0.6, patches: 1.4, kindBias: { stain: 2.2 } },
  trashy_corner: { decals: 2.1,  paint: 0.3, patches: 1.0, kindBias: { debris: 2.5 } },
  clean_pad:     { decals: 0.55, paint: 0.5, patches: 0.5, kindBias: {} },   // 23.09b: never naked
};
const ZONE_ORDER: ZoneName[] = ['open_lane', 'cracked_lot', 'garage_stain', 'trashy_corner', 'clean_pad'];
const ZONE_WU = 8192;

// 23.13 — name lookup exported for the prop system (zone-biased placement).
export function zoneNameAt(wx: number, wy: number, seed: number): ZoneName {
  const zx = Math.floor(wx / ZONE_WU), zy = Math.floor(wy / ZONE_WU);
  const rng = cellRng(zx, zy, seed, 777);
  // cracked_lot is the default character; others weighted rarer
  const roll = rng();
  return roll < 0.30 ? 'cracked_lot'
    : roll < 0.50 ? 'open_lane'
    : roll < 0.68 ? 'garage_stain'
    : roll < 0.86 ? 'trashy_corner'
    : 'clean_pad';
}
export type { ZoneName };
function zoneAt(wx: number, wy: number, seed: number): ZoneDef {
  void ZONE_ORDER;
  return ZONES[zoneNameAt(wx, wy, seed)];
}
const DECAL_WEIGHT_TOTAL = DECALS.reduce((s, d) => s + d.weight, 0);

export function chunkAssetUrls(): string[] {
  return [...BASE_TILE_URLS.map(t => t.url), ...PATCH_URLS, ...DECALS.map(d => d.url),
    '/map/deadlot/props/fcluster_02_v3.webp', '/map/deadlot/props/fcluster_03_v3.webp'];   // 23.44 family canopy
}

// ── seeded RNG for a world-space cell (deterministic, cross-chunk safe) ──
// 23.13: exported for worldProps (same guarantees needed there).
export function cellRng(cx: number, cy: number, seed: number, salt: number): () => number {
  let s = (Math.imul(cx, 0x9E3779B1) ^ Math.imul(cy, 0x85EBCA6B)
    ^ Math.imul(seed | 1, 0xC2B2AE35) ^ Math.imul(salt, 0x27D4EB2F)) >>> 0;
  return () => {
    s = (s + 0x6D2B79F5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// ── BETA 23.08 · P2 POI DRESSING ────────────────────────────────────────────
// Machines/shrines register here at mapgen; each chunk bakes wear + grime
// around any POI it touches, so interactables sit on used-looking ground
// instead of floating on clean asphalt (pack doc 09 "POI dressing").
export interface GroundPoi { x: number; y: number; kind: 'machine' | 'shrine' }
let _groundPois: GroundPoi[] = [];
export function setGroundPois(pois: GroundPoi[]): void {
  _groundPois = pois;
  invalidatePathNetwork();   // 23.41 — POI spur trails depend on these
  _cache.clear();
}
// 23.13 — prop placement keeps clear of the same POIs.
export function getGroundPois(): GroundPoi[] { return _groundPois; }

import { drawPathsIntoChunk, distToNearestPath, distToNearestPark, invalidatePathNetwork, getPlaces, getMachineStations } from './paths';   // 23.23/24 · 23.58 ground kits (runtime-only calls — cycle-safe)
import { paintPlaceGround, paintStationPad, clearGroundKitCache } from './groundKits';   // 23.60 procedural ground material kits · 23.61 station pads
import { MAP_FOREST, isForestBlockedAt, forestClearance, onForestAnchorsChanged } from './forest';   // 23.41 G1 (runtime-only — cycle-safe)
import { forestHemPoints } from './worldProps';   // 23.46 (runtime-only — cycle-safe): tree-base ground integration
import { drawVeinBasesIntoChunk } from './surgeVeins';   // 23.26 (runtime-only — cycle-safe)
import { MAP_BIOMES, BIOME_DEFS, biomeWeightsAt } from './biomes';   // G2 — pure fns, cycle-safe

type GetImage = (url: string) => HTMLImageElement | null;

// 23.16 — baked NE drop shadow for VOLUMETRIC decals (bones/rocks/trash):
// dark silhouette (#160034 — Wesnoth's law, never black), softened, drawn
// offset up-right under the decal at chunk-bake time. Flat stains skip it.
const _decalShadowCache = new Map<string, HTMLCanvasElement>();
function _getDecalShadow(url: string, img: HTMLImageElement): HTMLCanvasElement | null {
  const hit = _decalShadowCache.get(url);
  if (hit) return hit;
  const w = Math.max(32, Math.round(img.naturalWidth / 4));
  const h = Math.max(32, Math.round(img.naturalHeight / 4));
  const sil = document.createElement('canvas');
  sil.width = w; sil.height = h;
  const s2 = sil.getContext('2d');
  if (!s2) return null;
  s2.drawImage(img, 0, 0, w, h);
  s2.globalCompositeOperation = 'source-in';
  s2.fillStyle = '#160034';
  s2.fillRect(0, 0, w, h);
  const soft = document.createElement('canvas');
  soft.width = w; soft.height = h;
  const f2 = soft.getContext('2d');
  if (!f2) return null;
  f2.filter = 'blur(2px)';
  f2.drawImage(sil, 0, 0);
  f2.filter = 'none';
  if (_decalShadowCache.size > 24) _decalShadowCache.clear();
  _decalShadowCache.set(url, soft);
  return soft;
}

interface CacheEntry { cv: HTMLCanvasElement; full: boolean }
const _cache = new Map<string, CacheEntry>();
// 23.41 — spawn/portal/extraction anchors carve forest clearings; when the
// engine registers them the baked canopy must rebuild.
onForestAnchorsChanged(() => _cache.clear());

// 23.41b — canopy crowns pre-tinted into 4 darkness tiers (deep woods get
// darker); baked once, reused by every chunk. Replaces per-stamp ctx.filter.
const _tintedCrowns = new Map<HTMLImageElement, HTMLCanvasElement[]>();
function _getTintedCrowns(imgs: HTMLImageElement[]): HTMLCanvasElement[][] {
  return imgs.map(img => {
    const hit = _tintedCrowns.get(img);
    if (hit) return hit;
    const tiers = [1, 0.85, 0.7, 0.57].map(b => {
      const cv = document.createElement('canvas');
      const w = Math.max(1, Math.round(img.naturalWidth / 2));
      const h = Math.max(1, Math.round(img.naturalHeight / 2));
      cv.width = w; cv.height = h;
      const cc = cv.getContext('2d')!;
      cc.drawImage(img, 0, 0, w, h);
      if (b < 1) {
        cc.globalCompositeOperation = 'multiply';
        cc.fillStyle = `rgb(${Math.round(255 * b)},${Math.round(255 * b)},${Math.round(255 * b)})`;
        cc.fillRect(0, 0, w, h);
        cc.globalCompositeOperation = 'destination-in';
        cc.drawImage(img, 0, 0, w, h);
      }
      return cv;
    });
    _tintedCrowns.set(img, tiers);
    return tiers;
  });
}

// round-feathered variants of the asphalt art (patch mode, not tile mode)
const _roundPatch = new Map<string, HTMLCanvasElement>();
function _getRoundPatch(url: string, getImg: GetImage): HTMLCanvasElement | null {
  const hit = _roundPatch.get(url);
  if (hit) return hit;
  const img = getImg(url);
  if (!img || !img.naturalWidth) return null;
  const S = 512;
  const cv = document.createElement('canvas');
  cv.width = S; cv.height = S;
  const c = cv.getContext('2d')!;
  c.drawImage(img, 0, 0, S, S);
  // radial feather: solid center, gone at 50% — fully inside the square so
  // rotation never exposes a hard corner
  const g = c.createRadialGradient(S / 2, S / 2, S * 0.20, S / 2, S / 2, S * 0.50);
  g.addColorStop(0, 'rgba(0,0,0,1)');
  g.addColorStop(0.62, 'rgba(0,0,0,0.85)');
  g.addColorStop(1, 'rgba(0,0,0,0)');
  c.globalCompositeOperation = 'destination-in';
  c.fillStyle = g;
  c.fillRect(0, 0, S, S);
  c.globalCompositeOperation = 'source-over';
  _roundPatch.set(url, cv);
  return cv;
}

/** Iterate world-space cells of `cellWu` whose content (reach `reachWu`)
 *  can intersect the chunk rect. */
function _forCells(
  chunkWX: number, chunkWY: number, cellWu: number, reachWu: number,
  fn: (cx: number, cy: number) => void,
) {
  const x0 = Math.floor((chunkWX - reachWu) / cellWu);
  const x1 = Math.floor((chunkWX + CHUNK_WU + reachWu) / cellWu);
  const y0 = Math.floor((chunkWY - reachWu) / cellWu);
  const y1 = Math.floor((chunkWY + CHUNK_WU + reachWu) / cellWu);
  for (let cy = y0; cy <= y1; cy++) for (let cx = x0; cx <= x1; cx++) fn(cx, cy);
}

function _buildChunk(chunkX: number, chunkY: number, seed: number, getImg: GetImage, lodPx: number): CacheEntry {
  const cv = document.createElement('canvas');
  cv.width = lodPx; cv.height = lodPx;
  const c = cv.getContext('2d')!;
  // 23.52 — far-zoom LOD: the whole bake below draws in 512px space; one base
  // scale here renders it into a smaller canvas (a chunk is ~30px on screen
  // at far dev zoom — 512px each was the memory blowout).
  if (lodPx !== CHUNK_PX) c.scale(lodPx / CHUNK_PX, lodPx / CHUNK_PX);
  let full = true;

  const chunkWX = chunkX * CHUNK_WU, chunkWY = chunkY * CHUNK_WU;
  const lx = (wu: number) => (wu - chunkWX) * PX_PER_WU;
  const ly = (wu: number) => (wu - chunkWY) * PX_PER_WU;

  // ── 23.52 FAR BAKE (lod ≤ 32px): a chunk is ~30px on screen out there —
  // decals/moods/canopy stamps are invisible, but their LAYER MATH still cost
  // ~9ms per chunk (2,400 chunks ≈ 21 SECONDS of hang while streaming). Far
  // tier paints only what reads from orbit: the clearance field itself —
  // dark forest mass vs carved corridors/pockets. 64 samples ≈ 0.3ms. ──
  if (lodPx <= 32) {
    const N = 8, cellPx = CHUNK_PX / N, cellWu = CHUNK_WU / N;
    for (let gy = 0; gy < N; gy++) {
      for (let gx = 0; gx < N; gx++) {
        const clr = MAP_FOREST
          ? forestClearance(chunkWX + (gx + 0.5) * cellWu, chunkWY + (gy + 0.5) * cellWu, seed)
          : 1;
        c.fillStyle = clr <= -260 ? '#070805'    // deep woods — solid mass
          : clr <= -60 ? '#0d0f09'
          : clr <= 0 ? '#15160f'                 // the brush lip
          : '#2f2d2a';                            // walkable — substrate tone
        c.fillRect(gx * cellPx, gy * cellPx, cellPx + 1, cellPx + 1);
      }
    }
    return { cv, full: true };
  }

  // ── L1: SUBSTRATE — painted seamless base tiles (23.09), flat-fill fallback ──
  if (MAPDEV.layers.base) {
    c.fillStyle = '#2f2d2a';
    c.fillRect(0, 0, CHUNK_PX, CHUNK_PX);   // fallback under everything
    const bt0x = Math.floor(chunkWX / BASE_TILE_WU), bt0y = Math.floor(chunkWY / BASE_TILE_WU);
    const perChunk = CHUNK_WU / BASE_TILE_WU;   // 2
    for (let ty = bt0y; ty < bt0y + perChunk; ty++) {
      for (let tx = bt0x; tx < bt0x + perChunk; tx++) {
        const rng = cellRng(tx, ty, seed, 313);
        let roll = rng() * BASE_TILE_WEIGHT_TOTAL;
        let pick = BASE_TILE_URLS[0];
        for (const t of BASE_TILE_URLS) { roll -= t.weight; if (roll <= 0) { pick = t; break; } }
        const img = getImg(pick.url);
        if (!img || !img.naturalWidth) { full = false; continue; }
        c.drawImage(img, lx(tx * BASE_TILE_WU), ly(ty * BASE_TILE_WU), BASE_TILE_WU * PX_PER_WU, BASE_TILE_WU * PX_PER_WU);
      }
    }
  }

  // ── L2: low-frequency brightness variation (1536wu cells, cross-chunk) ──
  if (MAPDEV.layers.brightness) _forCells(chunkWX, chunkWY, 1536, 1400, (cx, cy) => {
    const rng = cellRng(cx, cy, seed, 11);
    const n = 1 + Math.floor(rng() * 2);
    for (let i = 0; i < n; i++) {
      const wx = (cx + rng()) * 1536, wy = (cy + rng()) * 1536;
      const r = (500 + rng() * 900) * PX_PER_WU;
      const lighten = rng() < 0.5;
      // 23.03 — lighten patches softened (they read as white smears under props)
      const a = lighten ? 0.03 + rng() * 0.04 : 0.04 + rng() * 0.06;
      const g = c.createRadialGradient(lx(wx), ly(wy), 0, lx(wx), ly(wy), r);
      g.addColorStop(0, lighten ? `rgba(210,200,180,${a})` : `rgba(5,5,12,${a + 0.02})`);
      g.addColorStop(1, 'rgba(0,0,0,0)');
      c.fillStyle = g;
      c.fillRect(lx(wx) - r, ly(wy) - r, r * 2, r * 2);
    }
  });

  // ── L4: hand-made asphalt art as organic round patches (1280wu cells) ──
  // 23.22 — KILLED per Dylan: "those circles that look like roads all over
  // the map... no shadows helping make that illusion real... some are close
  // together and would never connect. remove those large circles and just
  // focus on building real paths." Layer gate forced off; the REAL path
  // system (23.23 ribbons: POI-to-POI, step-up edge shadows, lamp-lined)
  // replaces what these circles were pretending to be.
  const L4_CIRCLES_REMOVED = true;
  if (!L4_CIRCLES_REMOVED && MAPDEV.layers.patches) _forCells(chunkWX, chunkWY, 1280, 800, (cx, cy) => {
    const rng = cellRng(cx, cy, seed, 42);
    const _zPatch = zoneAt((cx + 0.5) * 1280, (cy + 0.5) * 1280, seed).patches;   // 23.08 zones
    if (rng() > 0.35 * Math.min(1.3, _zPatch)) return;   // 23.09: substrate carries the material now — patches are accents
    const url = PATCH_URLS[Math.floor(rng() * PATCH_URLS.length)];
    const patch = _getRoundPatch(url, getImg);
    const wx = (cx + rng()) * 1280, wy = (cy + rng()) * 1280;
    const sizeWu = 700 + rng() * 800;
    // 23.08 foreshortening: squash BEFORE rotate (scale applied outside the
    // rotation) so the ellipse axis stays screen-horizontal like a real
    // tilted-camera ground mark; rotation limited to ±20°.
    const rot = (rng() - 0.5) * 0.7;
    const alpha = 0.75 + rng() * 0.2;   // 23.09: read as material, not smudge
    if (!patch) { full = false; return; }
    const sPx = sizeWu * PX_PER_WU;
    c.save();
    c.globalAlpha = alpha;
    c.translate(lx(wx), ly(wy));
    c.scale(1, GROUND_SQUASH);
    c.rotate(rot);
    c.drawImage(patch, -sPx / 2, -sPx / 2, sPx, sPx);
    c.restore();
  });

  // ── L4.5 (G2): BIOME WASH + LEAF LITTER — the biome layer's ground read.
  // World-aligned 128wu cells (never chunk-aligned) so adjacent chunks paint
  // byte-identical values over the border: seamless by construction, same
  // law as the 23.61 decal seeds. Weights crossfade over the ~600wu blend
  // band — there is no hard biome line on the ground, per Dylan's mockups. ──
  if (MAP_BIOMES) {
    const BW_CELL = 128;
    const bx0 = Math.floor(chunkWX / BW_CELL) * BW_CELL;
    const by0 = Math.floor(chunkWY / BW_CELL) * BW_CELL;
    for (let wy = by0; wy < chunkWY + CHUNK_WU; wy += BW_CELL) {
      for (let wx = bx0; wx < chunkWX + CHUNK_WU; wx += BW_CELL) {
        for (const { id, w } of biomeWeightsAt(wx + BW_CELL / 2, wy + BW_CELL / 2, seed)) {
          const def = BIOME_DEFS[id];
          if (w < 0.03) continue;
          if (def.wash.a > 0) {
            c.fillStyle = `rgba(${def.wash.r},${def.wash.g},${def.wash.b},${(def.wash.a * w).toFixed(3)})`;
            c.fillRect(lx(wx), ly(wy), BW_CELL * PX_PER_WU, BW_CELL * PX_PER_WU);
          }
          const nLitter = Math.round(def.litter * w);
          if (nLitter > 0) {
            // rng keyed by WORLD cell index; cells tile chunks exactly, so a
            // fleck near a chunk edge can overhang up to ~3px into territory
            // the neighbor bakes flat — cosmetic, same class as the L3
            // speckle layer's accepted behavior (Mariah fleet note 2026-07-06).
            // Per-biome salt so blend cells don't stack paper EXACTLY on
            // leaves (shared stream); forest KEEPS 5151 = shipped cells
            // byte-identical.
            const rng = cellRng(wx / BW_CELL, wy / BW_CELL, seed, 5151 + (id === 'forest' ? 0 : id === 'neighborhood' ? 17 : 34));
            for (let i = 0; i < nLitter; i++) {
              const fx = wx + rng() * BW_CELL, fy = wy + rng() * BW_CELL;
              const s = 1 + rng() * 2.2;
              // G3 N1 — per-biome litter palette: leaves in the woods,
              // paper/trash in the neighborhood (def-driven, one renderer)
              const [lr, lg, lb] = def.litterBase, [jr, jg, jb] = def.litterJitter;
              c.fillStyle = `rgba(${lr + Math.floor(rng() * jr)},${lg + Math.floor(rng() * jg)},${lb + Math.floor(rng() * jb)},${0.16 + rng() * 0.18})`;
              c.beginPath();
              c.ellipse(lx(fx), ly(fy), s, s * 0.55, rng() * Math.PI, 0, Math.PI * 2);
              c.fill();
            }
          }
        }
      }
    }
  }

  // ── L4.6 (23.26): SURGE VEIN dark crack bases (the pulsing purple glow
  // is LIVE in the engine — a bake can't pulse) ──
  if (MAP_SURGE_VEINS) drawVeinBasesIntoChunk(c, chunkX, chunkY, seed, lx, ly);

  // ── L3 (after patches so speckles unify them): tiny noise, chunk-local
  // (uniform density everywhere → borderless by construction) ──
  if (MAPDEV.layers.speckles) {
    const rng = cellRng(chunkX, chunkY, seed, 7);
    for (let i = 0; i < 420; i++) {
      const x = rng() * CHUNK_PX, y = rng() * CHUNK_PX;
      const light = rng() < 0.45;
      c.fillStyle = light
        ? `rgba(200,192,175,${0.03 + rng() * 0.05})`
        : `rgba(0,0,0,${0.04 + rng() * 0.06})`;
      const s = 0.7 + rng() * 1.6;
      c.fillRect(x, y, s, s);
    }
  }

  // ── L5: faded road / parking paint fragments (3072wu cells, long dashed
  // strokes that cross chunk borders; axis-ish angles like a real lot) ──
  if (MAPDEV.layers.paint) _forCells(chunkWX, chunkWY, 3072, 2600, (cx, cy) => {
    const rng = cellRng(cx, cy, seed, 99);
    const _zPaint = zoneAt((cx + 0.5) * 3072, (cy + 0.5) * 3072, seed).paint;   // 23.08 zones
    if (rng() > 0.4 * _zPaint) return;
    const n = 1 + Math.floor(rng() * 2);
    for (let i = 0; i < n; i++) {
      const wx = (cx + rng()) * 3072, wy = (cy + rng()) * 3072;
      const horiz = rng() < 0.5;
      const ang = (horiz ? 0 : Math.PI / 2) + (rng() - 0.5) * 0.06;
      const len = (900 + rng() * 1500) * PX_PER_WU;
      const wid = (12 + rng() * 8) * PX_PER_WU;
      const yellow = rng() < 0.3;
      const a = 0.08 + rng() * 0.09;
      c.save();
      c.translate(lx(wx), ly(wy));
      c.scale(1, GROUND_SQUASH);   // 23.08 foreshortening
      c.rotate(ang);
      c.fillStyle = yellow ? `rgba(215,180,80,${a})` : `rgba(225,220,205,${a})`;
      // worn paint: broken into dashes with seeded gaps
      let t = -len / 2;
      while (t < len / 2) {
        const seg = (60 + rng() * 240) * PX_PER_WU;
        if (rng() < 0.8) c.fillRect(t, -wid / 2, Math.min(seg, len / 2 - t), wid);
        t += seg + rng() * 40 * PX_PER_WU;
      }
      c.restore();
    }
  });

  // ── L5.5 (23.30): REAL PATHS — moved AFTER the paint layer (Dylan: "dirt
  // road shouldn't have lines in the middle like a regular road") — the old
  // parking-lot paint dashes were baking ON TOP of the trails; now the dirt
  // buries any paint it crosses.
  // ── L5.4 (23.46) · GROUND VARIATION PATCHES (Dylan: "the ground outside
  // the path is too flat and empty... damp mud, dead leaf beds, compacted
  // dirt, mossy areas, ashy dirt"). Big soft world-space blobs in 5 ground
  // moods; leaf-beds + moss concentrate along the forest transition ring so
  // the woods bleed into the world. ──
  if (MAP_FOREST) {
    const MOODS = [
      { fill: '47,38,28',  aMax: 0.20, nearForest: false },   // damp mud
      { fill: '58,48,28',  aMax: 0.22, nearForest: true  },   // dead-leaf bed
      { fill: '30,26,20',  aMax: 0.22, nearForest: false },   // compacted dirt
      { fill: '52,56,36',  aMax: 0.18, nearForest: true  },   // mossy green-brown
      { fill: '56,54,50',  aMax: 0.14, nearForest: false },   // ashy grey
    ];
    const GSTEP = 340;
    const gx0 = Math.floor((chunkWX - 400) / GSTEP), gx1 = Math.ceil((chunkWX + CHUNK_WU + 400) / GSTEP);
    const gy0 = Math.floor((chunkWY - 400) / GSTEP), gy1 = Math.ceil((chunkWY + CHUNK_WU + 400) / GSTEP);
    for (let gy = gy0; gy <= gy1; gy++) for (let gx = gx0; gx <= gx1; gx++) {
      const rng = cellRng(gx, gy, seed, 9393);
      if (rng() < 0.35) continue;
      const wx = gx * GSTEP + (rng() - 0.5) * GSTEP;
      const wy = gy * GSTEP + (rng() - 0.5) * GSTEP;
      const clr = forestClearance(wx, wy, seed);
      const mood = MOODS[Math.floor(rng() * MOODS.length)];
      // transition-ring bias: leafy/mossy moods love the woods' skirt
      if (mood.nearForest && (clr > 220 || clr < -260)) continue;
      if (!mood.nearForest && clr < -180) continue;
      const rx = (140 + rng() * 260) * PX_PER_WU;
      const x = lx(wx), y = ly(wy);
      const g = c.createRadialGradient(x, y, rx * 0.15, x, y, rx);
      const a = mood.aMax * (0.6 + rng() * 0.4);
      g.addColorStop(0, `rgba(${mood.fill},${a})`);
      g.addColorStop(1, `rgba(${mood.fill},0)`);
      c.save();
      c.translate(x, y); c.scale(1, GROUND_SQUASH); c.translate(-x, -y);
      c.fillStyle = g;
      c.fillRect(x - rx, y - rx, rx * 2, rx * 2);
      c.restore();
    }
  }

  if (MAP_PATHS) drawPathsIntoChunk(c, chunkX, chunkY, seed, lx, ly, PX_PER_WU);

  // ── L5.6 (23.60) · PROCEDURAL GROUND KITS (Dylan's ground-kits pack: "read
  // from the ground first, props second"). Layered material painter in
  // groundKits.ts: soft base with a rim falloff (the transition band, no hard
  // ellipse edge) + per-kit decal families. Painted AFTER paths so the kit
  // owns its room, BEFORE the underbrush skirt so the treeline still frames it. ──
  if (MAP_PATHS) {
    for (const pl of getPlaces(seed)) {
      // 23.61 — margin 60 → 160: the biome-blend rim dusts kit material up to
      // ~0.12·rx PAST the ellipse rim; a place just outside the old margin
      // could own decals inside this chunk (border pop otherwise).
      if (pl.x + pl.rx < chunkWX - 160 || pl.x - pl.rx > chunkWX + CHUNK_WU + 160 ||
          pl.y + pl.ry < chunkWY - 160 || pl.y - pl.ry > chunkWY + CHUNK_WU + 160) continue;
      paintPlaceGround(c, pl, seed, lx, ly, PX_PER_WU);
    }
  }

  // ── L5.65 (23.61) · CODED STATION PADS — the Wild Cards machine stands on
  // painted ground (roadside concrete slab / junkyard steel-plate pad / camp
  // plank-edged dirt), replacing the st_pad_concrete sprite. Painted after
  // the place kits so the pad owns its footprint; the POI wear ring + grime
  // (P2, below) still land ON TOP, so every station reads used. ──
  if (MAP_PATHS) {
    for (const st of getMachineStations(seed)) {
      if (st.x < chunkWX - 260 || st.x > chunkWX + CHUNK_WU + 260 ||
          st.y < chunkWY - 260 || st.y > chunkWY + CHUNK_WU + 260) continue;
      paintStationPad(c, st, seed, lx, ly, PX_PER_WU, GROUND_SQUASH);
    }
  }

  // ── L5.7 (23.46) · UNDERBRUSH SKIRT + CANOPY DAPPLE ──
  // The dark scrubby band under the treeline (bushes, litter, low foliage)
  // + soft canopy shade falling onto the transition — kills the "tree line
  // cuts too abruptly into the paths" read.
  if (MAP_FOREST) {
    const USTEP = 52;
    const ux0 = Math.floor((chunkWX - 80) / USTEP), ux1 = Math.ceil((chunkWX + CHUNK_WU + 80) / USTEP);
    const uy0 = Math.floor((chunkWY - 80) / USTEP), uy1 = Math.ceil((chunkWY + CHUNK_WU + 80) / USTEP);
    for (let uy = uy0; uy <= uy1; uy++) for (let ux = ux0; ux <= ux1; ux++) {
      const rng = cellRng(ux, uy, seed, 8484);
      const roll = rng();
      const wx = ux * USTEP + (rng() - 0.5) * USTEP;
      const wy = uy * USTEP + (rng() - 0.5) * USTEP;
      const clr = forestClearance(wx, wy, seed);
      const x = lx(wx), y = ly(wy);
      if (clr <= -2) {   // 23.50: darkening begins AT the line, not 12wu late
        // 23.47 — DARK FOREST FLOOR (Dylan: "should be very clear where the
        // collision is"): the ground under the woods ramps to near-black,
        // so any floor visible between trunks reads as deep shadow, never
        // as walkable lot. This + the tree density IS the collision telegraph.
        const d01 = Math.min(1, (-clr - 2) / 180);
        const rw2 = (34 + (roll * 20)) * PX_PER_WU;
        const g2 = c.createRadialGradient(x, y, rw2 * 0.25, x, y, rw2);
        g2.addColorStop(0, `rgba(8,9,6,${0.16 + d01 * 0.42})`);
        g2.addColorStop(1, 'rgba(8,9,6,0)');
        c.fillStyle = g2;
        c.fillRect(x - rw2, y - rw2 * 0.6, rw2 * 2, rw2 * 1.2);
      }
      if (clr <= 0 && clr >= -110) {
        // UNDERBRUSH: dark foliage mounds + leaf litter, denser deeper.
        // 23.50 — the FIRST 30wu are a GUARANTEED near-continuous brush lip
        // (Dylan's video: invisible walls on clean ground; the lip IS the
        // "fence made of brush" — visible exactly where movement stops).
        const depth01 = Math.min(1, -clr / 110);
        const lip = clr >= -30;
        if (lip || roll < 0.28 + depth01 * 0.45) {
          const rw = (11 + rng() * 22) * PX_PER_WU;
          const g = c.createRadialGradient(x, y, rw * 0.2, x, y, rw);
          const tone = rng() < 0.6 ? '26,31,18' : '32,30,20';
          g.addColorStop(0, `rgba(${tone},${0.34 + depth01 * 0.3})`);
          g.addColorStop(1, `rgba(${tone},0)`);
          c.fillStyle = g;
          c.fillRect(x - rw, y - rw * 0.6, rw * 2, rw * 1.2);
        }
        if (rng() < 0.3) {   // leaf dabs
          c.fillStyle = `rgba(${58 + Math.floor(rng() * 22)},${48 + Math.floor(rng() * 16)},26,${0.3 + rng() * 0.3})`;
          for (let d2 = 0; d2 < 3; d2++) c.fillRect(x + (rng() - 0.5) * 22, y + (rng() - 0.5) * 12, 1.6, 1.1);
        }
      }
      // 23.48 — DIRECTIONAL EDGE MURK: on SOUTH-FACING forest edges (walkable
      // ground to the NORTH) the wall's sprite bottoms face the camera —
      // bury them in extra litter + shadow so no repeated-base curtain shows.
      if (clr <= -4 && clr >= -85 && forestClearance(wx, wy - 105, seed) > 25) {
        const rw3 = (24 + roll * 26) * PX_PER_WU;
        const g3 = c.createRadialGradient(x, y, rw3 * 0.2, x, y, rw3);
        g3.addColorStop(0, 'rgba(12,13,8,0.42)');
        g3.addColorStop(0.7, 'rgba(16,17,10,0.2)');
        g3.addColorStop(1, 'rgba(12,13,8,0)');
        c.fillStyle = g3;
        c.fillRect(x - rw3, y - rw3 * 0.55, rw3 * 2, rw3 * 1.1);
        if (rng() < 0.5) {   // litter dabs on top of the murk
          c.fillStyle = `rgba(${44 + Math.floor(rng() * 18)},${38 + Math.floor(rng() * 12)},22,${0.4 + rng() * 0.25})`;
          for (let d3 = 0; d3 < 4; d3++) c.fillRect(x + (rng() - 0.5) * 34, y + (rng() - 0.5) * 14, 1.8, 1.2);
        }
      }
      if (clr > -6 && clr < 96 && roll < 0.16) {
        // CANOPY DAPPLE: soft tree-shade blobs spilling onto the walkable skirt
        const rw = (16 + rng() * 26) * PX_PER_WU;
        const g = c.createRadialGradient(x, y, rw * 0.2, x, y, rw);
        g.addColorStop(0, 'rgba(10,12,8,0.13)');
        g.addColorStop(1, 'rgba(10,12,8,0)');
        c.fillStyle = g;
        c.fillRect(x - rw, y - rw * 0.6, rw * 2, rw * 1.2);
      }
    }
    // ── L5.8 · TREE-BASE INTEGRATION: every hem tree gets a root mound,
    // leaf scatter and root strokes baked at its exact base point (the
    // SHARED generator guarantees agreement with the live sprite) ──
    for (const hp of forestHemPoints(chunkWX, chunkWY, seed)) {
      if (hp.kind === 'low') continue;   // 23.48: low pieces ARE the ground mess
      const rng = cellRng(Math.round(hp.x), Math.round(hp.y), seed, 9911);
      const x = lx(hp.x), y = ly(hp.y);
      const rw = (hp.kind === 'edgeCluster' ? 26 : 17 + rng() * 9) * PX_PER_WU * hp.scale;
      const g = c.createRadialGradient(x, y, rw * 0.15, x, y, rw);
      g.addColorStop(0, 'rgba(24,20,14,0.5)');
      g.addColorStop(0.7, 'rgba(24,20,14,0.24)');
      g.addColorStop(1, 'rgba(24,20,14,0)');
      c.save();
      c.translate(x, y); c.scale(1, 0.45); c.translate(-x, -y);
      c.fillStyle = g;
      c.fillRect(x - rw, y - rw, rw * 2, rw * 2);
      c.restore();
      // roots crawling out
      c.save();
      c.strokeStyle = 'rgba(38,30,20,0.75)';
      c.lineWidth = 1.6;
      const nRoot = 2 + Math.floor(rng() * 3);
      for (let r2 = 0; r2 < nRoot; r2++) {
        const a2 = rng() * Math.PI;   // fan downward-ish
        const ln = (7 + rng() * 10) * PX_PER_WU;
        c.beginPath();
        c.moveTo(x, y - 1);
        c.quadraticCurveTo(x + Math.cos(a2) * ln * 0.5, y + 1, x + Math.cos(a2) * ln, y + Math.sin(a2) * ln * 0.3 + 1);
        c.stroke();
      }
      // leaf scatter at the base
      for (let d2 = 0; d2 < 5; d2++) {
        c.fillStyle = `rgba(${52 + Math.floor(rng() * 20)},${44 + Math.floor(rng() * 14)},24,${0.35 + rng() * 0.3})`;
        c.fillRect(x + (rng() - 0.5) * 30, y + (rng() - 0.2) * 10, 1.6, 1.1);
      }
      c.restore();
    }
  }

  // ── L6: keyed decals — 512wu cells with REGION-VARIED density ──
  if (MAPDEV.layers.decals) _forCells(chunkWX, chunkWY, 512, 400, (cx, cy) => {
    // density comes from a coarser 4096wu "neighborhood" so some areas are
    // busy, others near-empty (pack: chunks must not all carry the same
    // amount of detail)
    const dRng = cellRng(Math.floor(cx / 8), Math.floor(cy / 8), seed, 133);
    const density = dRng();
    const zone = zoneAt((cx + 0.5) * 512, (cy + 0.5) * 512, seed);   // 23.08 zones
    const rng = cellRng(cx, cy, seed, 201);
    const n = Math.floor(rng() * 3 * density * 1.4 * zone.decals);
    // zone-biased weighted pick (kind multipliers skew what shows up here)
    const zw = DECALS.map(d => d.weight * (zone.kindBias[d.kind] ?? 1));
    const zwTotal = zw.reduce((s, v) => s + v, 0);
    // 23.16 — Dylan's screenshot caught two decal sins: (1) two of the SAME
    // decal overlapping into one unreadable mega-blob (no spacing), and
    // (2) mirror-flipped twins ("two versions of the same sprite") — flips
    // are BANNED anyway, they reverse the baked light. Spacing check within
    // the cell + retry; flips removed.
    const placed: Array<{ x: number; y: number; r: number; url: string }> = [];
    for (let i = 0; i < n; i++) {
      let roll = rng() * zwTotal;
      let pick = DECALS[0];
      for (let k = 0; k < DECALS.length; k++) { roll -= zw[k]; if (roll <= 0) { pick = DECALS[k]; break; } }
      // 23.08 foreshortening: rotation locked to ±20°, squash outside rotate
      const rot = (rng() - 0.5) * 0.7;
      const scl = 0.6 + rng() * 0.8;
      const alpha = 0.55 + rng() * 0.4;
      const img = getImg(pick.url);
      if (!img || !img.naturalWidth) { full = false; continue; }
      const dwPx = pick.wu * scl * PX_PER_WU;
      const dhPx = dwPx * (img.naturalHeight / img.naturalWidth);
      const reach = Math.max(dwPx, dhPx);
      const rad = pick.wu * scl * 0.5;
      let wx = 0, wy = 0, ok = false;
      for (let attempt = 0; attempt < 4 && !ok; attempt++) {
        wx = (cx + rng()) * 512; wy = (cy + rng()) * 512;
        ok = true;
        for (const q of placed) {
          const need = (rad + q.r) * (pick.url === q.url ? 1.7 : 0.7);   // 23.25: same decal twice = much farther apart (Dylan: bones "too many near each other")
          const ddx = wx - q.x, ddy = wy - q.y;
          if (ddx * ddx + ddy * ddy < need * need) { ok = false; break; }
        }
      }
      if (!ok) continue;
      // 23.24 — decals keep off the dirt trails + park plazas too
      if (MAP_PATHS && (distToNearestPath(wx, wy, seed) < 86 + rad || distToNearestPark(wx, wy, seed) < 340 + rad)) continue;   // 23.43: road 118 wide
      if (MAP_FOREST && isForestBlockedAt(wx, wy, seed)) continue;   // 23.41: no litter under the canopy
      placed.push({ x: wx, y: wy, r: rad, url: pick.url });
      const x = lx(wx), y = ly(wy);
      if (x < -reach || x > CHUNK_PX + reach || y < -reach || y > CHUNK_PX + reach) continue;
      // 23.16 — debris piles (bones, rocks, trash) have VOLUME, so they get
      // a small NE drop shadow baked under them (flat stains/paint don't).
      if (pick.kind === 'debris') {
        const sil = _getDecalShadow(pick.url, img);
        if (sil) {
          const off = Math.max(2, dwPx * 0.05);
          c.save();
          c.globalAlpha = alpha * 0.5;
          c.globalCompositeOperation = 'multiply';
          c.translate(x + off, y - off);
          c.scale(1, GROUND_SQUASH);
          c.rotate(rot);
          c.drawImage(sil, -dwPx / 2, -dhPx / 2, dwPx, dhPx);
          c.restore();
        }
      }
      c.save();
      c.globalAlpha = alpha;
      c.translate(x, y);
      c.scale(1, GROUND_SQUASH);
      c.rotate(rot);
      c.drawImage(img, -dwPx / 2, -dhPx / 2, dwPx, dhPx);
      c.restore();
    }
  });

  // ── P2: POI dressing (23.08) — wear ring + a few extra grime decals ──
  // 23.18 — Dylan's "vertical line" screenshot exposed TWO bugs here:
  // (1) this block existed TWICE (the pre-foreshortening version survived
  // the Step-0 edit) — every POI drew a double wear ring, and the stale
  // copy's UNSQUASHED round ring read as a tall dark band; (2) the 300wu
  // cull margin was smaller than the dressing's true reach (~315wu:
  // 180 dist + decal half-size), so the neighboring chunk skipped a POI
  // the first chunk drew → hard straight cut at the chunk border. One
  // block, 420wu margin.
  for (let pi = 0; pi < _groundPois.length; pi++) {
    const poi = _groundPois[pi];
    if (poi.x < chunkWX - 420 || poi.x > chunkWX + CHUNK_WU + 420 ||
        poi.y < chunkWY - 420 || poi.y > chunkWY + CHUNK_WU + 420) continue;
    const rng = cellRng(Math.round(poi.x), Math.round(poi.y), seed, 555);
    const px = lx(poi.x), py = ly(poi.y);
    // worn dark ellipse under/around the POI (foreshortened like all ground)
    const rr = (poi.kind === 'machine' ? 150 : 130) * PX_PER_WU;
    const g = c.createRadialGradient(px, py, rr * 0.2, px, py, rr);
    g.addColorStop(0, 'rgba(10,10,12,0.16)');
    g.addColorStop(0.7, 'rgba(10,10,12,0.08)');
    g.addColorStop(1, 'rgba(10,10,12,0)');
    c.save();
    c.translate(px, py);
    c.scale(1, GROUND_SQUASH);
    c.translate(-px, -py);
    c.fillStyle = g;
    c.fillRect(px - rr, py - rr, rr * 2, rr * 2);
    c.restore();
    // 2-3 grime decals scattered beside it (machine: trash/oil; shrine: cracks/scorch)
    const wants: DecalKind[] = poi.kind === 'machine' ? ['debris', 'stain', 'debris'] : ['crack', 'stain'];
    for (const kind of wants) {
      if (rng() < 0.25) continue;
      const cands = DECALS.filter(d => d.kind === kind);
      const pick = cands[Math.floor(rng() * cands.length)];
      const img = getImg(pick.url);
      if (!img || !img.naturalWidth) { full = false; continue; }
      const ang = rng() * Math.PI * 2;
      const dist = (90 + rng() * 90) * PX_PER_WU;
      const dwPx = pick.wu * (0.5 + rng() * 0.4) * PX_PER_WU;
      const dhPx = dwPx * (img.naturalHeight / img.naturalWidth);
      c.save();
      c.globalAlpha = 0.5 + rng() * 0.3;
      c.translate(px + Math.cos(ang) * dist, py + Math.sin(ang) * dist * GROUND_SQUASH);
      c.scale(1, GROUND_SQUASH);
      c.rotate((rng() - 0.5) * 0.7);
      c.drawImage(img, -dwPx / 2, -dhPx / 2, dwPx, dhPx);
      c.restore();
    }
  }

  // ── L8 (23.41): G1 FOREST CANOPY — the impassable mass, baked ────────────
  // World-aligned stamp grid so neighboring chunks draw IDENTICAL crowns in
  // their overlap margins (seamless borders, same trick as every layer).
  // Interior of the blocked field only; the walkable-boundary "hem" is left
  // to live y-sorted tree props (worldProps) so the player walks behind
  // foliage at the edge.
  if (MAP_FOREST) {
    // 23.43 — OBLIQUE canopy clusters (Dylan: "trees look like bushes... top
    // down 90 won't work in 2.5D"): tops + shadowed front faces = HEIGHT.
    const CROWN_URLS = ['/map/deadlot/props/fcluster_02_v3.webp', '/map/deadlot/props/fcluster_03_v3.webp'];   // 23.44: one style family
    const crowns = CROWN_URLS.map(u => getImg(u));
    if (crowns.some(i => !i || !i.naturalWidth)) {
      full = false;
    } else {
      const STEP = 118;
      const MARGIN = 320;
      // 23.41b — ctx.filter forced the slow raster path (10 FPS bake bursts);
      // pre-tint each crown into 4 darkness tiers ONCE and pick by depth.
      const tinted = _getTintedCrowns(crowns as HTMLImageElement[]);
      const ix0 = Math.floor((chunkWX - MARGIN) / STEP), ix1 = Math.ceil((chunkWX + CHUNK_WU + MARGIN) / STEP);
      const iy0 = Math.floor((chunkWY - MARGIN) / STEP), iy1 = Math.ceil((chunkWY + CHUNK_WU + MARGIN) / STEP);
      for (let iy = iy0; iy <= iy1; iy++) {          // top→bottom: lower rows overlap upper
        for (let ix = ix0; ix <= ix1; ix++) {
          const rng = cellRng(ix, iy, seed, 8181);
          const wx = ix * STEP + (rng() - 0.5) * STEP * 1.1;
          const wy = iy * STEP + (rng() - 0.5) * STEP * 1.1;
          const clr = forestClearance(wx, wy, seed);
          if (clr > -470) continue;                   // 23.44: live tree bands own -0..-470; bake starts DEEPER than any sprite can reach (no fake overlaps, ever)
          const depth = -clr;
          const tier = depth > 1050 ? 3 : depth > 620 ? 2 : depth > 280 ? 1 : 0;
          // 23.52 — POSITIVE modulo: ix/iy go NEGATIVE west/north of the
          // origin, and JS % keeps the sign → tinted[-1] → the crash that
          // killed the whole game loop at far zoom (Dylan's black screen)
          const img = tinted[(((ix * 7 + iy * 13) % tinted.length) + tinted.length) % tinted.length][tier];
          const sizeWu = 235 + rng() * 105;
          const dwPx = sizeWu * PX_PER_WU;
          const dhPx = dwPx * (img.height / img.width);
          const x = lx(wx), y = ly(wy);
          if (x < -dwPx || x > CHUNK_PX + dwPx || y < -dhPx * 1.2 || y > CHUNK_PX + dhPx) continue;
          // 23.43 — BOTTOM-ANCHORED: crowns rise UP from their ground point,
          // rows overlap top→bottom = the forest finally has HEIGHT
          c.drawImage(img, x - dwPx / 2, y - dhPx, dwPx, dhPx);
        }
      }
    }
  }

  return { cv, full };
}

// 23.52 — FAR-ZOOM LOD TIERS (Dylan: "when i zoom out far and look around my
// screen goes black and the game breaks"). At dev zoom 0.03 the view spans
// ~2,400 chunks; with CACHE_CAP 48 every chunk missed the cache EVERY frame,
// so ~2,400 full 512px bakes ran per frame (~2GB of canvas allocations) until
// the browser dropped the canvas context — the black screen, dead until
// refresh. Out there a chunk is only ~30px on screen, so far tiers bake the
// SAME art into tiny canvases, the cache grows to hold the whole view, and
// builds are budgeted per frame (unbuilt chunks show a flat ground/forest
// tone for a moment while the world streams in). Zoom ≥ 0.42 keeps today's
// exact behavior — normal gameplay (1.0–1.7) and the intro reveal (0.45)
// never enter the far tiers.
// F7 (co-op audit blocker #1): tier-0 was budget:Infinity → every chunk-border
// crossing baked ALL missing chunks in one frame (measured: 13.6ms/bake, 122ms
// teleport spikes). Now finite + a PREFETCH RING below, so chunks bake BEFORE
// they enter view and misses show the placeholder tone for a frame or two
// instead of freezing the sim. Cap 48→64: the ring widens the working set
// (~35 chunks at zoom 0.42) — headroom stops ring-thrash.
// F7.2 (4P world-budget gate — both legs failed on stacked bakes): steady
// streaming is ≤1 sync bake/frame TOTAL (doctrine #4); the ring bakes
// VELOCITY-ORDERED (the chunk most ahead of travel first) and only on frames
// with no interior bake. A COLD view (teleport / floor advance / intro,
// ≥ _COLD_MISSES interior misses) is a screen transition, not gameplay
// streaming — it keeps the proven F7 recovery burst (3/frame + 20ms box) so
// spawns don't crawl in as a placeholder mosaic chunk-by-chunk.
const _LOD_TIERS = [
  { minZoom: 0.42, px: CHUNK_PX, cap: 64, budget: 1 },
  { minZoom: 0.15, px: 128, cap: 340, budget: 12 },   // full bake ≈ 9ms each — keep streaming under ~110ms/frame
  { minZoom: 0,    px: 32,  cap: 4200, budget: 64 },
];
const _COLD_MISSES = 4;     // interior misses at/above this = cold view, not streaming
const _COLD_BUDGET = 3;     // the F7 recovery numbers (measured 38ms teleport worst)
let _lodPx = CHUNK_PX;
// F7.2 — view-center track for velocity-ordered prefetch (wu)
let _pfLastCx = NaN, _pfLastCy = NaN;
let _pfDirX = 0, _pfDirY = 0;
// flat placeholder tone per chunk (one clearance sample, remembered forever)
const _phTone = new Map<string, string>();

/**
 * Draw the endless ground. Call INSIDE the camera transform (world space).
 * Chunks cache once complete; incomplete chunks (art still loading) rebuild
 * each frame until everything lands. `zoom` picks the LOD tier (23.52).
 */
export function drawChunkedGround(
  ctx: CanvasRenderingContext2D,
  viewLeft: number, viewTop: number, viewRight: number, viewBottom: number,
  seed: number,
  getImg: GetImage,
  zoom: number = 1,
): void {
  const lod = _LOD_TIERS.find(t => zoom >= t.minZoom) ?? _LOD_TIERS[_LOD_TIERS.length - 1];
  if (lod.px !== _lodPx) { _lodPx = lod.px; _cache.clear(); }   // tier switch (dev zoom event) — rebake
  let budget = lod.budget;
  const _t0 = performance.now();
  let _built = 0, _ph = 0;

  const c0x = Math.floor(viewLeft / CHUNK_WU), c1x = Math.floor(viewRight / CHUNK_WU);
  const c0y = Math.floor(viewTop / CHUNK_WU),  c1y = Math.floor(viewBottom / CHUNK_WU);

  // F7.2 — velocity track + cold-view detection (tier-0 only).
  const _tier0 = lod.px === CHUNK_PX;
  if (_tier0) {
    const vcx = (viewLeft + viewRight) / 2, vcy = (viewTop + viewBottom) / 2;
    if (Number.isFinite(_pfLastCx)) {
      const mdx = vcx - _pfLastCx, mdy = vcy - _pfLastCy;
      const m = Math.hypot(mdx, mdy);
      if (m > CHUNK_WU * 3) { _pfDirX = 0; _pfDirY = 0; }        // teleport — old heading is a lie
      else if (m > 1) { _pfDirX = mdx / m; _pfDirY = mdy / m; }  // moving — remember heading
    }
    _pfLastCx = vcx; _pfLastCy = vcy;
    // pre-scan: how many VISIBLE chunks are missing? ≥ _COLD_MISSES means a
    // teleport/spawn transition — allow the F7 recovery burst for this frame.
    let misses = 0;
    for (let cy = c0y; cy <= c1y && misses < _COLD_MISSES; cy++) {
      for (let cx = c0x; cx <= c1x; cx++) {
        const e = _cache.get(`${cx},${cy}`);
        if (!e || !e.full) { if (++misses >= _COLD_MISSES) break; }
      }
    }
    if (misses >= _COLD_MISSES) budget = _COLD_BUDGET;
  }

  for (let cy = c0y; cy <= c1y; cy++) {
    for (let cx = c0x; cx <= c1x; cx++) {
      const key = `${cx},${cy}`;
      let entry = _cache.get(key);
      if (!entry || !entry.full) {
        // F7 time-box: always allow the FIRST bake (a visible hole must fill),
        // but stop stacking further bakes past ~20ms of ground-pass time.
        if (budget > 0 && (_built === 0 || performance.now() - _t0 < 20)) {
          budget--; _built++;
          entry = _buildChunk(cx, cy, seed, getImg, lod.px);
          if (entry.full) {
            if (_cache.size >= lod.cap) {
              const first = _cache.keys().next().value;
              if (first !== undefined) _cache.delete(first);
            }
            _cache.set(key, entry);
          }
        } else {
          entry = undefined;   // over budget this frame — placeholder below
        }
      } else {
        _cache.delete(key); _cache.set(key, entry);   // LRU touch
      }
      if (entry) {
        // +0.5px overdraw kills the sub-pixel hairline between cached canvases
        ctx.drawImage(entry.cv, cx * CHUNK_WU - 0.5, cy * CHUNK_WU - 0.5, CHUNK_WU + 1, CHUNK_WU + 1);
      } else {
        // 23.55 (audit find) — tone cache key must include the SEED: floor
        // advance regenerates the world with a new seed, and stale tones from
        // the old world would paint the wrong forest shape.
        const phKey = `${key},${seed}`;
        let tone = _phTone.get(phKey);
        if (tone === undefined) {
          if (_phTone.size > 30000) _phTone.clear();
          const mid = CHUNK_WU / 2;
          tone = forestClearance(cx * CHUNK_WU + mid, cy * CHUNK_WU + mid, seed) <= 0
            ? '#0b0c08'    // the dark-forest-floor family — far view keeps its shape
            : '#2f2d2a';   // the substrate fallback tone
          _phTone.set(phKey, tone);
        }
        ctx.fillStyle = tone;
        ctx.fillRect(cx * CHUNK_WU - 0.5, cy * CHUNK_WU - 0.5, CHUNK_WU + 1, CHUNK_WU + 1);
        _ph++;
      }
    }
  }
  // F7 · PREFETCH RING, F7.2 · VELOCITY-ORDERED — bake the missing ring chunk
  // most AHEAD of travel (dot of chunk offset with the view-center heading),
  // one per frame, and ONLY on frames with no interior bake: total sync bakes
  // never exceed 1/frame in steady streaming (doctrine #4). Idle heading →
  // nearest-first, so the ring still warms while standing still.
  if (_tier0 && _built === 0) {
    let bestCx = 0, bestCy = 0, bestKey = '', bestScore = -Infinity;
    for (let cy = c0y - 1; cy <= c1y + 1; cy++) {
      for (let cx = c0x - 1; cx <= c1x + 1; cx++) {
        if (cx >= c0x && cx <= c1x && cy >= c0y && cy <= c1y) continue;   // interior already handled
        const key = `${cx},${cy}`;
        const e = _cache.get(key);
        if (e && e.full) continue;
        const ox = (cx + 0.5) * CHUNK_WU - _pfLastCx;
        const oy = (cy + 0.5) * CHUNK_WU - _pfLastCy;
        const om = Math.hypot(ox, oy) || 1;
        const score = (_pfDirX !== 0 || _pfDirY !== 0)
          ? (ox * _pfDirX + oy * _pfDirY) / om   // moving: most-ahead first
          : -om;                                  // idle: nearest first
        if (score > bestScore) { bestScore = score; bestCx = cx; bestCy = cy; bestKey = key; }
      }
    }
    if (bestScore > -Infinity) {
      _built++;
      const built = _buildChunk(bestCx, bestCy, seed, getImg, lod.px);
      if (built.full) {
        if (_cache.size >= lod.cap) {
          const first = _cache.keys().next().value;
          if (first !== undefined) _cache.delete(first);
        }
        _cache.set(bestKey, built);
      }
    }
  }
  // dev telemetry for the far-zoom investigation (harmless in prod: one
  // object write per frame, warn only fires on a genuinely broken frame)
  const _ms = performance.now() - _t0;
  const _g = globalThis as unknown as Record<string, unknown>;
  (_g as { __cfCalls?: number }).__cfCalls = ((_g as { __cfCalls?: number }).__cfCalls ?? 0) + 1;
  _g.__chunkFrame =
    { calls: (_g as { __cfCalls?: number }).__cfCalls, zoom: Math.round(zoom * 1000) / 1000, ms: Math.round(_ms), built: _built, placeholders: _ph, cache: _cache.size, lodPx: lod.px };
  if (_ms > 400) console.warn(`[chunks] slow ground pass: ${Math.round(_ms)}ms (built ${_built}, ph ${_ph}, cache ${_cache.size}, lod ${lod.px})`);
}

/** BETA 23.06 — cached chunk count for the perf overlay. */
export function chunkCacheSize(): number {
  return _cache.size;
}

/** Clear the chunk cache (floor advance / new run). */
export function clearChunkCache(): void {
  _cache.clear();
  _phTone.clear();   // 23.55 (audit find) — placeholder tones must not survive a world regen
  clearGroundKitCache();   // 23.60 — ground-kit decals are world-seeded too
}
